import { AppError } from "@mebi/shared";
import { translateErrorCode } from "@mebi/api-client";
import { toast } from "@mebi/ui";

/**
 * Dịch lỗi sang tiếng Việt
 * Uses @mebi/api-client's translator for ErrorCodes
 * Falls back to string matching for legacy/unknown errors
 *
 * @param error - Lỗi cần dịch (AppError, Error, string, unknown)
 * @returns Tin nhắn lỗi đã dịch (tiếng Việt)
 */
const ERROR_MAPPINGS: Array<{ match: string | string[]; result: string }> = [
  {
    match: "invalid email or password",
    result: "Email hoặc mật khẩu không chính xác",
  },
  {
    match: ["invalid phone number or password", "invalid phone or password"],
    result: "Số điện thoại hoặc mật khẩu không chính xác",
  },
  {
    match: ["email not verified", "email_not_verified"],
    result: "Email chưa được xác thực. Vui lòng xác thực trước khi đăng nhập",
  },
  {
    match: [
      "phone number not verified",
      "phone_number_not_verified",
      "phone verification required",
    ],
    result:
      "Số điện thoại chưa được xác thực. Vui lòng xác thực trước khi đăng nhập",
  },
  { match: "user not found", result: "Tài khoản không tồn tại" },
  {
    match: ["invalid code", "otp code is invalid"],
    result: "Mã xác thực không chính xác",
  },
  { match: ["expired", "code is expired"], result: "Mã xác thực đã hết hạn" },
  {
    match: ["too many requests", "rate limit"],
    result: "Bạn thao tác quá nhiều, vui lòng chờ một lát",
  },
  {
    match: "already exists",
    result: "Số điện thoại hoặc Email đã được đăng ký",
  },
  { match: "invalid format", result: "Định dạng dữ liệu không hợp lệ" },
  { match: "validation", result: "Vui lòng kiểm tra lại thông tin nhập vào" },
];

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;

  if (typeof error === "object" && error !== null) {
    const errObj = error as Record<string, unknown>;
    if (typeof errObj?.message === "string") return errObj.message;
    if (typeof errObj?.error === "string") return errObj.error;
    if (typeof errObj?.code === "string") return errObj.code;
  }
  return "";
}

export function translateError(error: unknown): string {
  if (!error) return "Đã có lỗi xảy ra";

  // 1. Nếu là AppError, lấy chính xác message đã được API-Client hoặc Server cấy vào.
  if (error instanceof AppError) {
    if (error.message) return error.message;
    // Fallback bảo hiểm nếu AppError rỗng
    return translateErrorCode(error.code);
  }

  const errorMessage = extractErrorMessage(error);

  if (!errorMessage) return "Đã có lỗi xảy ra";

  if (
    /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i.test(
      errorMessage,
    )
  ) {
    return errorMessage;
  }

  const lowerMsg = errorMessage.toLowerCase();
  for (const mapping of ERROR_MAPPINGS) {
    const isMatch = Array.isArray(mapping.match)
      ? mapping.match.some((m) => lowerMsg.includes(m))
      : lowerMsg.includes(mapping.match);
    if (isMatch) return mapping.result;
  }

  // Fallback
  return errorMessage.length < 50
    ? errorMessage
    : "Đã có lỗi xảy ra. Mời thử lại sau.";
}

type NotifyOptions = {
  title?: string;
  description?: string;
};

export const notifyError = (
  error: unknown,
  options?: NotifyOptions,
): string => {
  const description = options?.description ?? translateError(error);
  toast({
    title: options?.title ?? "Có lỗi xảy ra",
    description,
    variant: "destructive",
  });
  return description;
};

export const notifySuccess = (
  message: string,
  options?: NotifyOptions,
): void => {
  toast({
    title: options?.title ?? "Thành công",
    description: options?.description ?? message,
    variant: "success",
  });
};

export default translateError;
